import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import { useKPIStore } from "../../stores/kpi.store";
import { getStatVisual, CATEGORY_COLOR } from "./statVisual";

/** Units whose suffix is already embedded by formatValue (or intentionally hidden). */
const SUFFIX_HIDDEN_UNITS = ["SAR", "SAR/kg", "kg/day", "km/L", "times/day"];

interface StatCardProps {
  title: string;
  value: number;
  unit: string;
  trend: number;
  /** When true, hide month-over-month trend (e.g. organization overview / lifetime dashboard). */
  hideTrend?: boolean;
  /** Smaller layout for nested metric grids (e.g. branch analysis cards). */
  compact?: boolean;
  /** Hide the category icon (e.g. nested metrics inside branch cards). */
  hideIcon?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  unit,
  trend,
  hideTrend = false,
  compact = false,
  hideIcon = false,
}) => {
  const theme = useTheme();
  const { limits } = useKPIStore();
  const [animatedValue, setAnimatedValue] = useState<number>(0);

  // Count up from 0 to the real value; larger numbers animate slightly slower.
  useEffect(() => {
    if (value <= 0) {
      setAnimatedValue(value);
      return;
    }

    let duration = 1000;
    if (value > 1000000) duration = 3000;
    else if (value > 100000) duration = 2500;
    else if (value > 10000) duration = 2000;
    else if (value > 1000) duration = 1500;

    const steps = 60;
    const increment = value / steps;
    const stepTime = duration / steps;

    let currentValue = 0;
    const timer = setInterval(() => {
      currentValue += increment;
      if (currentValue >= value) {
        setAnimatedValue(value);
        clearInterval(timer);
      } else {
        setAnimatedValue(currentValue);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [value]);

  const kpiLimit = limits.find(
    (limit) =>
      limit.name === title.replace(/\s+/g, "_").toUpperCase() && limit.isEnabled
  );

  const getProgressColor = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case ">":
      case ">=":
        return value >= limit
          ? theme.palette.success.main
          : theme.palette.error.main;
      case "<":
      case "<=":
        return value <= limit
          ? theme.palette.success.main
          : theme.palette.error.main;
      case "=":
        return Math.abs(value - limit) < 0.01
          ? theme.palette.success.main
          : theme.palette.error.main;
      default:
        return accent;
    }
  };

  const getProgressValue = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case ">":
      case ">=":
      case "<":
      case "<=":
        return Math.min((value / limit) * 100, 100);
      case "=":
        return Math.abs(value - limit) < 0.01 ? 100 : 0;
      default:
        return 0;
    }
  };

  const getProgressLabel = (value: number, limit: number, operator: string) => {
    const symbols: Record<string, string> = {
      ">": ">",
      "<": "<",
      ">=": "≥",
      "<=": "≤",
      "=": "=",
    };
    const symbol = symbols[operator];
    if (!symbol) return "";
    return `${value.toLocaleString()} ${symbol} ${limit.toLocaleString()} ${unit}`;
  };

  // Format the value based on the unit (capped at 2 decimals to avoid overflow).
  const formatValue = (value: number, unit: string) => {
    const limitedValue = Math.round(value * 100) / 100;

    if (unit === "hours" || unit === "%" || unit === "times/day") {
      return limitedValue.toFixed(1);
    }
    if (unit === "SAR") {
      return limitedValue.toLocaleString("en-US", {
        style: "currency",
        currency: "SAR",
      });
    }
    if (unit === "SAR/kg") {
      return `${limitedValue.toLocaleString("en-US", {
        style: "currency",
        currency: "SAR",
      })}/kg`;
    }
    if (unit === "kg/day") {
      return `${limitedValue.toLocaleString()} kg/day`;
    }
    if (unit === "km/L") {
      return limitedValue.toFixed(2);
    }
    return limitedValue.toLocaleString();
  };

  const { Icon, category } = getStatVisual(title, unit);
  const accent = CATEGORY_COLOR[category](theme);
  const isEmpty = value === 0;
  const showUnitSuffix = unit !== "" && !SUFFIX_HIDDEN_UNITS.includes(unit);
  const trendColor =
    trend > 0 ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 2 : 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: compact ? 1 : 1.5,
        borderRadius: "14px",
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: "border-color 0.2s ease",
        "&:hover": {
          borderColor: alpha(theme.palette.text.primary, 0.18),
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: compact ? 1.25 : 1.5 }}>
        {!hideIcon && (
          <Icon
            sx={{
              flexShrink: 0,
              fontSize: compact ? 22 : 26,
              color: accent,
            }}
          />
        )}

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: theme.palette.text.secondary }}
          >
            {title}
          </Typography>

          <Box
            sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 0.5 }}
          >
            {isEmpty ? (
              <Tooltip title="This data is being calculated, come back later">
                <Typography
                  sx={{
                    fontSize: compact ? 22 : 30,
                    fontWeight: 650,
                    lineHeight: 1.1,
                    color: theme.palette.text.disabled,
                    cursor: "help",
                  }}
                >
                  —
                </Typography>
              </Tooltip>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: compact ? 22 : 30,
                    fontWeight: 650,
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {formatValue(animatedValue, unit)}
                </Typography>
                {showUnitSuffix && (
                  <Typography variant="body2" color="text.secondary">
                    {unit}
                  </Typography>
                )}
              </>
            )}
          </Box>

          {!hideTrend && !isEmpty && trend !== 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mt: 0.75,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  color: trendColor,
                }}
              >
                {trend > 0 ? (
                  <TrendingUpIcon sx={{ fontSize: 16 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16 }} />
                )}
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {trend > 0 ? "+" : "-"}
                  {Math.abs(trend).toFixed(1)}%
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.disabled }}
              >
                vs last month
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {!compact && kpiLimit && (
        <Box sx={{ mt: "auto" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.75,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              KPI Target
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getProgressLabel(value, kpiLimit.value, kpiLimit.operator)}
            </Typography>
          </Box>
          <Tooltip title={kpiLimit.alertMessage}>
            <LinearProgress
              variant="determinate"
              value={getProgressValue(value, kpiLimit.value, kpiLimit.operator)}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  backgroundColor: getProgressColor(
                    value,
                    kpiLimit.value,
                    kpiLimit.operator
                  ),
                },
              }}
            />
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
};

export default StatCard;
